import { BackgroundCheckConfig } from '@/backend/features/background-check/defaults';
import { BackgroundCheckColumns, BackgroundCheckData, backgroundCheckEntityName } from '@/backend/features/background-check/shared';
import { BackgroundCheckType, backgroundCheckTypes } from '@/backend/features/background-check/types';
import { backgroundCheckStatusToIconNameMap } from '@/ui/utils';
import { ValueProvider } from '@juvare/common-form-ui/ValueProvider';
import { StorageClient } from '@juvare/platform-sdk-js/StorageClient';
import { SettingColumns, SettingData, settingEntityName } from '@juvare/platform-sdk-react/backend/features/editable-settings/shared';

const backgroundCheckActiveTypeValueProvider: ValueProvider = {
    type: 'backgroundCheckEnabledTypes',
    label: 'Background Check Enabled Types',
    getValues: () => {
        const storage = new StorageClient<SettingData, SettingColumns>('setting');
        return storage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckConfig',
                    },
                ],
            })
            .then((results) => {
                const [backgroundCheckTypeRow] = results.rows;

                if (!backgroundCheckTypeRow) {
                    throw new Error('Background check type is required');
                }

                const backgroundCheckTypesSetting: BackgroundCheckConfig = JSON.parse(backgroundCheckTypeRow.columns.value);

                return Object.entries(backgroundCheckTypesSetting.type)
                    .filter(([, config]) => config.enabled)
                    .map(([type, config]) => {
                        return {
                            value: type,
                            label: config.label,
                        };
                    });
            });
    },
};

const backgroundCheckActiveValidStatusTypeValueProvider: ValueProvider = {
    type: 'backgroundCheckEnabledValidTypes',
    label: 'Background Check Enabled Valid Types',
    getValues: (accountId: string) => {
        const settingsStorage = new StorageClient<SettingData, SettingColumns>('setting');
        const bcStorage = new StorageClient<BackgroundCheckData, BackgroundCheckColumns>(backgroundCheckEntityName);

        return settingsStorage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckConfig',
                    },
                ],
            })
            .then((results) => {
                const [backgroundCheckTypeRow] = results.rows;

                if (!backgroundCheckTypeRow) {
                    throw new Error('Background check type is required');
                }

                return bcStorage
                    .list({
                        filter: [
                            {
                                column: 'status',
                                op: 'not-in',
                                value: ['didNotConsent', 'formIncomplete'],
                            },
                            {
                                column: 'accountId',
                                op: '=',
                                value: accountId,
                            },
                        ],
                    })
                    .then((validBackgroundChecks) => {
                        const backgroundCheckTypesSetting: BackgroundCheckConfig = JSON.parse(backgroundCheckTypeRow.columns.value);

                        return Object.entries(backgroundCheckTypesSetting.type)
                            .filter(([type, config]) => {
                                const isEnabled = config.enabled;
                                const validStatus = validBackgroundChecks.rows.some(
                                    (validBackgroundCheck) => validBackgroundCheck.columns.type === type,
                                );

                                return isEnabled && validStatus;
                            })
                            .map(([type, config]) => {
                                return {
                                    value: type,
                                    label: config.label,
                                };
                            });
                    });
            });
    },
};

const backgroundCheckJasEnabledTypeValueProvider: ValueProvider = {
    type: 'backgroundCheckJasEnabledTypes',
    label: 'Background Check Types',
    getValues: () => {
        const storage = new StorageClient<SettingData, SettingColumns>('setting');
        return storage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckConfig',
                    },
                ],
            })
            .then((results) => {
                const [backgroundCheckTypeRow] = results.rows;

                if (!backgroundCheckTypeRow) {
                    throw new Error('Background check type is required');
                }

                const backgroundCheckTypesSetting: BackgroundCheckConfig = JSON.parse(backgroundCheckTypeRow.columns.value);

                return Object.entries(backgroundCheckTypesSetting.type)
                    .filter(([type, config]) => config.enabled && backgroundCheckTypes[type as BackgroundCheckType]?.jasType)
                    .map(([type, config]) => {
                        return {
                            value: type,
                            label: config.label,
                        };
                    });
            });
    },
};

const backgroundCheckStatusValueProvider = {
    type: 'backgroundCheckStatuses',
    label: 'Background Check Statuses',
    getValues: (backgroundCheckType?: BackgroundCheckType) => {
        const storage = new StorageClient<SettingData, SettingColumns>(settingEntityName);
        return storage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckConfig',
                    },
                ],
            })
            .then((results) => {
                const [backgroundCheckTypeRow] = results.rows;

                if (!backgroundCheckTypeRow) {
                    throw new Error('Background check type setting row is required');
                }

                if (!backgroundCheckType) {
                    throw new Error('Background check type setting is required');
                }

                const backgroundCheckTypesSetting: BackgroundCheckConfig = JSON.parse(backgroundCheckTypeRow.columns.value);

                return backgroundCheckTypesSetting.type[backgroundCheckType].statuses.map((status) => {
                    return {
                        value: status.status,
                        label: status.label,
                    };
                });
            });
    },
};

const backgroundCheckEnabledStatusValueProvider: ValueProvider = {
    type: 'backgroundCheckEnabledStatuses',
    label: 'Background Check Enabled Statuses',
    getValues: (backgroundCheckType?: BackgroundCheckType) => {
        const storage = new StorageClient<SettingData, SettingColumns>('setting');
        return storage
            .list({
                filter: [
                    {
                        column: 'name',
                        op: '=',
                        value: 'backgroundCheckConfig',
                    },
                ],
            })
            .then((results) => {
                const [backgroundCheckTypeRow] = results.rows;

                if (!backgroundCheckTypeRow) {
                    throw new Error('Background check type setting row is required');
                }

                if (!backgroundCheckType) {
                    throw new Error('Background check type setting row is required');
                }

                const backgroundCheckTypesSetting: BackgroundCheckConfig = JSON.parse(backgroundCheckTypeRow.columns.value);

                return backgroundCheckTypesSetting.type[backgroundCheckType].statuses
                    .filter(({ enabled }) => enabled)
                    .map(({ status, label }) => {
                        return {
                            value: status,
                            label,
                            icon: backgroundCheckStatusToIconNameMap[status ?? 'unknown'],
                        };
                    });
            });
    },
};

export const backgroundCheckValueProviders = [
    backgroundCheckActiveTypeValueProvider,
    backgroundCheckActiveValidStatusTypeValueProvider,
    backgroundCheckJasEnabledTypeValueProvider,
    backgroundCheckStatusValueProvider,
    backgroundCheckEnabledStatusValueProvider,
];
